import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { tickerApi } from '../../services/tickerApi';
import TabBar from '../../components/Common/TabBar';
import { UpArrowIcon, DownArrowIcon } from '../../components/Icons';
import { useAuth } from '../../contexts/AuthContext';
import { LockIcon } from '../../components/StockTable';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass, getStatisticLabelSizeClass, getSmallTextSizeClass } from '../../utils/fontSizeUtils';
import { saveUserTickerPreferences, loadUserTickerPreferences } from '../../utils/tickerPreferencesUtils';
import Footer from '../../components/Footer';
import CategoryInfoModal from '../../components/CategoryInfoModal/CategoryInfoModal';
import IndicatorInfoModal from '../../components/IndicatorInfoModal/IndicatorInfoModal';
import {
  FUNDAMENTAL_STATISTICS,
  ANNUAL_FUNDAMENTAL_STATISTICS,
  FUNDAMENTAL_CATEGORY_HEADERS,
  ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS,
} from '../../utils/fundamentalsTabUtils';
import {
  generateQuarterlyColumns as generateQuarterlyColumnsUtil,
  generateAnnualColumns as generateAnnualColumnsUtil,
  getFundamentalsValue as getFundamentalsValueUtil,
  formatFundamentalsValue as formatFundamentalsValueUtil,
  getFundamentalsArrowInfo as getFundamentalsArrowInfoUtil,
  getFundamentalsArrowTooltip as getFundamentalsArrowTooltipUtil,
  type FinancialData,
  type RatioData,
} from '../../utils/tickerFundamentalsUtils';
import { publicListApi } from '../../services/publicListApi';
import { useTickerPageMeta } from '../../hooks/useTickerPageMeta';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

// FinancialData and RatioData types are now imported from tickerFundamentalsUtils

interface TickerFundamentalsProps {}

const TickerFundamentals: React.FC<TickerFundamentalsProps> = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const timeframeParam = searchParams.get('timeframe');
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();
  
  // Pro-only statistics (always show PRO tag, regardless of subscription status)
  const PRO_ONLY_STATISTICS = [
    'ev_fcf_ratio',
    'cash_flow_margin',
    'ocf_ratio',
    'fcf_yield',
    'roic',
  ];
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  const isPublicList = !!publicUuid;
  
  // Determine locked statistics based on pro subscription status (for actually locking values)
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tickerName, setTickerName] = useState<string>(() => {
    const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
    return cachedName || '';
  });
  const [tickerSymbol, setTickerSymbol] = useState<string>(() => {
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
    return cachedSymbol ?? ticker ?? '';
  });
  const [marketType, setMarketType] = useState<string | null>(() => {
    const cachedMarketType = sessionStorage.getItem(`ticker-market-type-${ticker}`);
    return cachedMarketType || null;
  });
  const [timeframe, setTimeframe] = useState<'quarterly' | 'annual'>(() => {
    // Use timeframe from URL parameter if available, otherwise default to 'quarterly'
    if (timeframeParam === 'quarterly' || timeframeParam === 'annual') {
      return timeframeParam;
    }
    return 'quarterly';
  });
  
  // Data state
  const [quarterlyFinancials, setQuarterlyFinancials] = useState<FinancialData[]>([]);

  // Set document title and meta description for ticker fundamentals page
  useTickerPageMeta({
    tickerSymbol,
    tickerName,
    pageType: 'fundamentals',
  });
  const [quarterlyRatios, setQuarterlyRatios] = useState<RatioData[]>([]);
  const [annualFinancials, setAnnualFinancials] = useState<FinancialData[]>([]);
  const [annualRatios, setAnnualRatios] = useState<RatioData[]>([]);
  
  // Column data
  const [quarterlyColumns, setQuarterlyColumns] = useState<Array<{ label: string; date: string; year: number; quarter: number }>>([]);
  const [annualColumns, setAnnualColumns] = useState<Array<{ label: string; date: string; year: number }>>([]);
  
  // Polling state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncPollingAttempts, setSyncPollingAttempts] = useState<number>(0);
  const [pollingLimitReached, setPollingLimitReached] = useState(false);
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  const tableScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Full screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);

  // Handle keyboard events for full screen mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen]);
  
  // State to track screen size for responsive column width
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  
  // Fetch public list data when publicUuid is present
  useEffect(() => {
    if (publicUuid && !publicListData) {
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          // If public list fetch fails, treat as if owner doesn't have PRO
          setPublicListData({
            ownerHasProSubscription: false,
          });
        });
    } else if (!publicUuid) {
      setPublicListData(null);
    }
  }, [publicUuid, publicListData]);
  
  // Update screen size on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Edit mode state
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    const saved = sessionStorage.getItem(`stocktable-ticker-fundamentals-hidden-statistics-${ticker}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  // Temporary state for editing mode (not saved until "Done" is clicked)
  const [tempHiddenStatistics, setTempHiddenStatistics] = useState<string[]>([]);
  
  // Category info modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  // Indicator info modal state
  const [isIndicatorModalOpen, setIsIndicatorModalOpen] = useState(false);
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState<string>('');

  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Function to stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
    setIsSyncing(false);
  };

  // Function to check sync status and fetch data
  const checkSyncAndFetchData = async (attemptNumber: number = 1) => {
    if (!ticker) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`TickerFundamentals: Attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopPolling();
      setPollingLimitReached(true);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`TickerFundamentals: checkSyncAndFetchData called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`TickerFundamentals: checkSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    isCheckingSyncRef.current = true;
    
    console.log(`TickerFundamentals: checkSyncAndFetchData called for ${ticker} (attempt ${attemptNumber})`);
    
    // Update global polling attempts for display purposes
    setSyncPollingAttempts(attemptNumber);

    try {
      console.log(`🚀 TickerFundamentals: About to call checkTickerSyncStatus for: ${ticker}`);
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [ticker],
      });
      
      console.log(`TickerFundamentals: Sync response for ${ticker}:`, syncResponse);
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === ticker);
      if (!tickerInfo) {
        throw new Error(`Ticker ${ticker} not found`);
      }
      
      setTickerBasicsId(tickerInfo.ticker_basics_id);
      const name = tickerInfo.ticker_name || ticker;
      const symbol = tickerInfo.ticker_symbol || ticker;
      setTickerName(name);
      setTickerSymbol(symbol);
      sessionStorage.setItem(`ticker-name-${ticker}`, name);
      sessionStorage.setItem(`ticker-symbol-${ticker}`, symbol);
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setLastUpdated(date);
        }
      }
      
      // Fetch market_type from ticker basics
      try {
        const tickerBasicsResponse = await tickerApi.getTickerBasics([ticker.toUpperCase()]);
        if (tickerBasicsResponse.success && tickerBasicsResponse.data && tickerBasicsResponse.data.length > 0) {
          const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === ticker.toUpperCase());
          if (tickerBasic && tickerBasic.marketType) {
            setMarketType(tickerBasic.marketType);
            sessionStorage.setItem(`ticker-market-type-${ticker}`, tickerBasic.marketType);
          }
        }
      } catch (err) {
        console.error('Error fetching ticker basics for market_type:', err);
        // Continue without market_type - will show full UI
      }
      
      // Check if ticker is synced
      if (tickerInfo.is_synced) {
        console.log(`TickerFundamentals: ${ticker} is synced, fetching data`);
        
        // Fetch both quarterly and annual data using the same methods as the original useEffect hooks
        const [quarterlyFinancialsResponse, quarterlyRatiosResponse] = await Promise.all([
          tickerApi.getPast2YearsQuarterlyFinancials(tickerInfo.ticker_basics_id),
          tickerApi.getPast2YearsQuarterlyRatios(tickerInfo.ticker_basics_id)
        ]);

        const [annualFinancialsResponse, annualRatiosResponse] = await Promise.all([
          tickerApi.getPast5YearsAnnualFinancials(tickerInfo.ticker_basics_id),
          tickerApi.getPast5YearsAnnualRatios(tickerInfo.ticker_basics_id)
        ]);

        if (quarterlyFinancialsResponse.success) {
          setQuarterlyFinancials(quarterlyFinancialsResponse.data);
        }
        
        if (quarterlyRatiosResponse.success) {
          setQuarterlyRatios(quarterlyRatiosResponse.data);
        }

        if (annualFinancialsResponse.success) {
          setAnnualFinancials(annualFinancialsResponse.data);
        }
        
        if (annualRatiosResponse.success) {
          setAnnualRatios(annualRatiosResponse.data);
        }

        // Generate quarterly columns
        await generateQuarterlyColumns(quarterlyFinancialsResponse.data, quarterlyRatiosResponse.data, ticker);
        
        // Generate annual columns
        generateAnnualColumns(annualFinancialsResponse.data, annualRatiosResponse.data);
        
        // Stop polling since we got the data
        stopPolling();
        setLoading(false);
        setError(null);
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        // If we've reached the polling limit, try to fetch data anyway
        if (attemptNumber >= 5) {
          console.warn(`Sync polling limit reached (5 attempts) for ticker: ${ticker}. Attempting to fetch data anyway.`);
          setPollingLimitReached(true);
          stopPolling();
          
          // Still try to fetch fundamental data even though sync status is false
          try {
            const [quarterlyFinancialsResponse, quarterlyRatiosResponse] = await Promise.all([
              tickerApi.getPast2YearsQuarterlyFinancials(tickerInfo.ticker_basics_id),
              tickerApi.getPast2YearsQuarterlyRatios(tickerInfo.ticker_basics_id)
            ]);

            const [annualFinancialsResponse, annualRatiosResponse] = await Promise.all([
              tickerApi.getPast5YearsAnnualFinancials(tickerInfo.ticker_basics_id),
              tickerApi.getPast5YearsAnnualRatios(tickerInfo.ticker_basics_id)
            ]);

            if (quarterlyFinancialsResponse.success) {
              setQuarterlyFinancials(quarterlyFinancialsResponse.data);
            }
            
            if (quarterlyRatiosResponse.success) {
              setQuarterlyRatios(quarterlyRatiosResponse.data);
            }

            if (annualFinancialsResponse.success) {
              setAnnualFinancials(annualFinancialsResponse.data);
            }
            
            if (annualRatiosResponse.success) {
              setAnnualRatios(annualRatiosResponse.data);
            }

            // Generate quarterly columns
            await generateQuarterlyColumns(quarterlyFinancialsResponse.data, quarterlyRatiosResponse.data, ticker);
            
            // Generate annual columns
            generateAnnualColumns(annualFinancialsResponse.data, annualRatiosResponse.data);
            
            setLoading(false);
            setError(null);
            console.log(`TickerFundamentals: Successfully fetched data for ${ticker} despite sync status being false`);
          } catch (fetchErr) {
            console.error(`TickerFundamentals: Failed to fetch data for ${ticker} after polling limit:`, fetchErr);
            setError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch ticker data');
            setLoading(false);
          }
          isCheckingSyncRef.current = false; // Reset flag
        } else {
          // Double-check we haven't stopped polling before setting up interval
          if (pollingStoppedRef.current) {
            console.log(`TickerFundamentals: Polling stopped, not setting up interval`);
            isCheckingSyncRef.current = false; // Reset flag before returning
            return;
          }
          
          console.log(`TickerFundamentals: ${ticker} is not synced yet (attempt ${attemptNumber}/5), continuing to poll`);
          setIsSyncing(true);
          
          // Update the current attempt ref so the interval callback knows the next attempt number
          currentAttemptRef.current = attemptNumber;
          
          // Continue polling with 2 second delay
          const pollingDelay = 2000; // 2 seconds
          
          // Clear any existing interval before creating a new one
          // This ensures we always use the latest attempt number
          clearPollingInterval();
          
          console.log(`Starting sync polling with ${pollingDelay}ms delay`);
          const interval = setInterval(() => {
            // Check if polling was stopped or we've reached the limit
            if (pollingStoppedRef.current) {
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              return;
            }
            
            // Get the next attempt number from the ref
            const nextAttempt = currentAttemptRef.current + 1;
            
            // Check if we've exceeded the limit
            if (nextAttempt > 5) {
              console.log(`TickerFundamentals: Reached attempt limit (5), stopping interval`);
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              pollingStoppedRef.current = true;
              return;
            }
            
            // Update the ref for the next iteration
            currentAttemptRef.current = nextAttempt;
            
            // Make the sync check call
            checkSyncAndFetchData(nextAttempt);
          }, pollingDelay);
          
          syncPollingIntervalRef.current = interval;
          
          // Reset flag after setting up interval
          isCheckingSyncRef.current = false;
        }
      }
    } catch (err) {
      console.error(`TickerFundamentals: Error in checkSyncAndFetchData:`, err);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(err) && err.response?.status === 429) ||
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`TickerFundamentals: Rate limit (429) detected for ${ticker}. Stopping polling and attempting to fetch data anyway.`);
        stopPolling();
        setPollingLimitReached(true);
        
        // Still try to fetch fundamental data even after rate limit error
        try {
          if (tickerBasicsId) {
            const [quarterlyFinancialsResponse, quarterlyRatiosResponse] = await Promise.all([
              tickerApi.getPast2YearsQuarterlyFinancials(tickerBasicsId),
              tickerApi.getPast2YearsQuarterlyRatios(tickerBasicsId)
            ]);

            const [annualFinancialsResponse, annualRatiosResponse] = await Promise.all([
              tickerApi.getPast5YearsAnnualFinancials(tickerBasicsId),
              tickerApi.getPast5YearsAnnualRatios(tickerBasicsId)
            ]);

            if (quarterlyFinancialsResponse.success) {
              setQuarterlyFinancials(quarterlyFinancialsResponse.data);
            }
            
            if (quarterlyRatiosResponse.success) {
              setQuarterlyRatios(quarterlyRatiosResponse.data);
            }

            if (annualFinancialsResponse.success) {
              setAnnualFinancials(annualFinancialsResponse.data);
            }
            
            if (annualRatiosResponse.success) {
              setAnnualRatios(annualRatiosResponse.data);
            }

            // Generate quarterly columns
            await generateQuarterlyColumns(quarterlyFinancialsResponse.data, quarterlyRatiosResponse.data, ticker);
            
            // Generate annual columns
            generateAnnualColumns(annualFinancialsResponse.data, annualRatiosResponse.data);
            
            setLoading(false);
            setError(null);
            console.log(`TickerFundamentals: Successfully fetched data for ${ticker} despite rate limit error`);
          } else {
            throw new Error('Ticker basics ID not available');
          }
        } catch (fetchErr) {
          console.error(`TickerFundamentals: Failed to fetch data for ${ticker} after rate limit error:`, fetchErr);
          setError('Rate limit exceeded. Please try again later.');
          setLoading(false);
        }
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        // For other errors, set error state normally (show 404 for invalid/non-existent ticker)
        const rawMessage = err instanceof Error ? err.message : 'Failed to fetch ticker data';
        const isTickerNotFound = /no valid ticker|valid tickers found/i.test(rawMessage);
        setError(isTickerNotFound ? '404 - Ticker not found' : rawMessage);
        stopPolling();
        setLoading(false);
        isCheckingSyncRef.current = false; // Reset flag
      }
    }
  };

  // Sync timeframe with URL parameter when URL changes (but not when state changes)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const currentTimeframeParam = urlParams.get('timeframe');
    if (currentTimeframeParam === 'quarterly' || currentTimeframeParam === 'annual') {
      if (timeframe !== currentTimeframeParam) {
        setTimeframe(currentTimeframeParam);
      }
    }
  }, [location.search]); // Only depend on location.search, not timeframe

  // Start polling process
  useEffect(() => {
    if (!ticker) return;

    // Reset polling state when ticker changes
    pollingStoppedRef.current = false;
    setPollingLimitReached(false);
    setSyncPollingAttempts(0);
    currentAttemptRef.current = 1; // Reset attempt counter
    isCheckingSyncRef.current = false; // Reset concurrent check flag
    clearPollingInterval(); // Clear any existing interval without setting stopped flag

    // Start the polling process
    setLoading(true);
    checkSyncAndFetchData(1);
  }, [ticker]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Load user ticker preferences when tickerBasicsId is available and user is authenticated
  useEffect(() => {
    const loadPreferences = async () => {
      if (isAuthenticated && tickerBasicsId) {
        try {
          const preferences = await loadUserTickerPreferences(tickerBasicsId, 'fundamentals');
          setHiddenStatistics(preferences.hiddenStatistics); // Always set, even if empty array
          
          if (preferences.hiddenStatistics.length > 0) {
            console.log('✅ Loaded fundamental ticker preferences:', preferences.hiddenStatistics);
          } else {
            console.log('✅ No fundamental ticker preferences found, showing all indicators');
          }
        } catch (error) {
          console.log('No fundamental ticker preferences found or error loading:', error);
          setHiddenStatistics([]); // Reset to show all indicators when no preferences
        }
      }
      // For unauthenticated users, keep the session storage values that were loaded during initialization
      // Don't reset them here as they were already loaded from sessionStorage in useState
    };

    loadPreferences();
  }, [isAuthenticated, tickerBasicsId]);


  // Generate quarterly columns using shared utility
  const generateQuarterlyColumns = async (financials: FinancialData[], ratios: RatioData[], tickerSymbol?: string) => {
    // Fetch latest daily date if ticker symbol is available
    let latestDailyDate: Date | null = null;
    if (tickerSymbol) {
      try {
        const dailyResponse = await tickerApi.getTickerDailyPrices(tickerSymbol, 0, 1, undefined, 'date', 'desc');
        if (dailyResponse.data?.daily_prices && dailyResponse.data.daily_prices.length > 0) {
          const latestDaily = dailyResponse.data.daily_prices[0];
          if (latestDaily.date) {
            latestDailyDate = new Date(latestDaily.date);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch latest daily date for quarterly columns:', error);
        // Continue without latest daily date
      }
    }
    const columns = generateQuarterlyColumnsUtil(financials, ratios, latestDailyDate);
    setQuarterlyColumns(columns);
  };

  // Generate annual columns using shared utility
  const generateAnnualColumns = (financials: FinancialData[], ratios: RatioData[]) => {
    const columns = generateAnnualColumnsUtil(financials, ratios);
    setAnnualColumns(columns);
  };

  // Get value for a specific statistic and period using shared utility
  const getValue = (statisticKey: string, column: { year: number; quarter?: number }) => {
    const financialData = timeframe === 'quarterly' ? quarterlyFinancials : annualFinancials;
    const ratioData = timeframe === 'quarterly' ? quarterlyRatios : annualRatios;
    return getFundamentalsValueUtil(statisticKey, column, financialData, ratioData, timeframe);
  };

  // Handler for lock icon clicks - navigate to pricing page (only for non-public lists)
  const handleLockIconClick = () => {
    if (!isPublicList) {
      navigate('/pricing', { state: { fromUpgrade: true } });
    }
  };

  // Format value based on type using shared utility
  const formatValue = (value: number | null, type: string, statisticKey?: string) => {
    // Check if this statistic is locked for non-pro users (only if value exists)
    if (statisticKey && lockedStatistics.includes(statisticKey) && value !== null && value !== undefined) {
      return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? handleLockIconClick : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
    }
    return formatFundamentalsValueUtil(value, type, statisticKey, loading || isSyncing);
  };

  // Get statistic color based on value (matching ListFundamentals)
  const getStatisticColor = (statisticKey: string, value: number | null): string => {
    // Use secondary text color for all fundamental values
    // Arrow colors provide the visual indication of good/bad changes
    return 'text-text-light-neutral dark:text-text-secondary';
  };

  // Reset horizontal scroll to left when switching tabs
  useEffect(() => {
    tableScrollContainerRef.current && (tableScrollContainerRef.current.scrollLeft = 0);
  }, [location.pathname]);

  // Edit mode handlers
  const handleStartEdit = () => {
    // Initialize temporary state with current saved state
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(true);
  };

  const handleCancelEdit = () => {
    // Reset temporary state
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };

  const handleDoneEdit = async () => {
    // Save temporary state to permanent state and sessionStorage
    setHiddenStatistics([...tempHiddenStatistics]);
    sessionStorage.setItem(`stocktable-ticker-fundamentals-hidden-statistics-${ticker}`, JSON.stringify(tempHiddenStatistics));
    
    // Save user ticker preferences for authenticated users (always save, even if empty array)
    if (isAuthenticated && tickerBasicsId) {
      try {
        await saveUserTickerPreferences(tickerBasicsId, 'fundamentals', tempHiddenStatistics);
        console.log('✅ Fundamental ticker preferences saved successfully');
      } catch (error) {
        console.error('❌ Failed to save fundamental ticker preferences:', error);
        // Don't throw error to prevent UI from breaking
      }
    }
    
    // Reset temporary state
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };

  // Handle category info click
  const handleCategoryInfoClick = (category: string) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory('');
  };

  // Handle indicator info click
  const handleIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedIndicatorKey(indicatorKey);
    setIsIndicatorModalOpen(true);
  };

  // Handle indicator modal close
  const handleCloseIndicatorModal = () => {
    setIsIndicatorModalOpen(false);
    setSelectedIndicatorKey('');
  };

  // Statistics visibility handlers (work with temporary state in edit mode)
  const handleHideStatistic = (statisticKey: string) => {
    if (isEditingColumns) {
      setTempHiddenStatistics(prev => [...prev, statisticKey]);
    } else {
      setHiddenStatistics(prev => [...prev, statisticKey]);
    }
  };

  const handleShowStatistic = (statisticKey: string) => {
    if (isEditingColumns) {
      setTempHiddenStatistics(prev => prev.filter(key => key !== statisticKey));
    } else {
      setHiddenStatistics(prev => prev.filter(key => key !== statisticKey));
    }
  };

  // Get arrow tooltip text using shared utility
  const getArrowTooltip = (statisticKey: string, currentValue: number | null, previousValue: number | null): string => {
    return getFundamentalsArrowTooltipUtil(statisticKey, currentValue, previousValue, timeframe);
  };

  // Get arrow direction and color using shared utility
  const getArrowInfo = (statisticKey: string, currentValue: number | null, previousValue: number | null): { direction: 'up' | 'down' | null, isGood: boolean } => {
    return getFundamentalsArrowInfoUtil(statisticKey, currentValue, previousValue, timeframe);
  };
  if (error) {
    return (
      <>
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        <Header sticky={false} />
        {publicUuid && !isAuthenticated && <PublicListPromoBar />}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
              <p className="text-gray-600 dark:text-text-secondary">{error}</p>
            </div>
          </div>
        </main>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
      </>
    );
  }

  const currentColumns = timeframe === 'quarterly' ? quarterlyColumns : annualColumns;
  const allStatistics = timeframe === 'quarterly' ? FUNDAMENTAL_STATISTICS : ANNUAL_FUNDAMENTAL_STATISTICS;
  const baseCategoryHeaders = timeframe === 'quarterly' ? FUNDAMENTAL_CATEGORY_HEADERS : ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS;
  
  // Calculate equal width for date columns (150px base, but can be adjusted)
  // Calculate responsive width for date columns - smaller on small screens, larger on big screens
  // This ensures all date columns have equal width while adapting to screen size
  const calculateDateColumnWidth = () => {
    if (isSmallScreen) {
      // Smaller width on small screens to allow more columns to fit
      return 120; // Reduced from 150px
    }
    // Standard width on larger screens
    return 150;
  };
  
  const dateColumnWidth = calculateDateColumnWidth();
  
  // Filter visible statistics
  // In edit mode, show all statistics (including hidden ones). In normal mode, only show non-hidden ones.
  // For ETF/ETV tickers, only show dividend_yield
  const isEtfOrEtv = marketType && (marketType.toUpperCase() === 'ETF' || marketType.toUpperCase() === 'ETV');
  const baseFilteredStatistics = isEditingColumns 
    ? allStatistics 
    : allStatistics.filter(stat => !hiddenStatistics.includes(stat.key));
  
  const currentStatistics = isEtfOrEtv
    ? baseFilteredStatistics.filter(stat => stat.key === 'dividend_yield')
    : baseFilteredStatistics;
  
  // Get the current hidden state (temporary in edit mode, permanent in normal mode)
  const currentHiddenStatistics = isEditingColumns ? tempHiddenStatistics : hiddenStatistics;
  
  // Debug logging
  console.log('TickerFundamentals Debug:', {
    isEditingColumns,
    hiddenStatistics,
    tempHiddenStatistics,
    currentHiddenStatistics,
    currentStatisticsLength: currentStatistics.length,
    allStatisticsLength: allStatistics.length
  });
  

  // Calculate statistics column width based on longest indicator name
  const calculateStatisticsColumnWidth = () => {
    const longestLabel = allStatistics.reduce((longest, stat) => 
      stat.label.length > longest.length ? stat.label : longest, ''
    );
    
    // Base width calculation: character count * 7px + padding + buffer (reduced from 8px)
    const baseWidth = longestLabel.length * 7; // ~7px per character (slightly tighter)
    const padding = 24; // 12px on each side (reduced from 16px)
    const buffer = 16; // Extra buffer to prevent cutoff (reduced from 20px)
    
    // Add extra width for checkbox when in edit mode
    const checkboxWidth = isEditingColumns ? 40 : 0; // 40px for checkbox + gap
    
    const calculatedWidth = baseWidth + padding + buffer + checkboxWidth;
    
    // Use smaller width on small screens (mobile-first approach)
    // On small screens, use a more compact calculation but not too narrow
    if (isSmallScreen) {
      // More compact calculation for small screens with better balance
      const compactBaseWidth = longestLabel.length * 6; // ~6px per character (slightly wider)
      const compactPadding = 12; // 6px on each side (px-1.5)
      const compactEditButtonSpace = isEditingColumns ? 28 : 0; // Slightly larger edit button space
      const compactBuffer = 12; // Slightly larger buffer
      
      return Math.max(100, compactBaseWidth + compactPadding + compactEditButtonSpace + compactBuffer); // Minimum 100px on small screens
    }
    
    // Cap the maximum width to prevent excessive gaps (reduced from unlimited)
    return Math.min(250, Math.max(120, calculatedWidth)); // Maximum 250px, minimum 120px on larger screens
  };
  
  const statisticsColumnWidth = calculateStatisticsColumnWidth();
  
  // Debug logging for width calculation
  console.log('TickerFundamentals - Statistics column width calculated:', statisticsColumnWidth);
  console.log('TickerFundamentals - Is small screen:', isSmallScreen);
  console.log('TickerFundamentals - Window width:', window.innerWidth);
  console.log('TickerFundamentals - Longest label:', allStatistics.reduce((longest, stat) => 
    stat.label.length > longest.length ? stat.label : longest, ''
  ));
  
  // Adjust category headers based on filtered statistics
  // For ETF/ETV tickers, don't show category headers
  const currentCategoryHeaders = isEtfOrEtv 
    ? []
    : baseCategoryHeaders.map(header => {
    if (isEditingColumns) {
      // In edit mode, use original startIndex since we're showing all statistics
      console.log(`Category "${header.label}": Edit mode - using original startIndex: ${header.startIndex}`);
      return {
        ...header,
        startIndex: header.startIndex
      };
    } else {
      // In normal mode, find the first visible statistic in this category
      const nextCategoryStartIndex = baseCategoryHeaders.find(h => h.startIndex > header.startIndex)?.startIndex || allStatistics.length;
      const categoryStatistics = allStatistics.slice(header.startIndex, nextCategoryStartIndex);
      
      // Find the first visible statistic in this category
      const firstVisibleStatistic = categoryStatistics.find(stat => !hiddenStatistics.includes(stat.key));
      
      if (!firstVisibleStatistic) {
        // No visible statistics in this category - hide the category
        console.log(`Category "${header.label}": No visible statistics`);
        return { 
          ...header, 
          startIndex: -1 
        };
      }
      
      // Find the position of this statistic in the filtered currentStatistics array
      const newStartIndex = currentStatistics.findIndex(stat => stat.key === firstVisibleStatistic.key);
      
      console.log(`Category "${header.label}": firstVisibleStatistic=${firstVisibleStatistic.key}, newStartIndex=${newStartIndex}`);
      
      return {
        ...header,
        startIndex: newStartIndex
      };
    }
  }).filter(header => {
    // In edit mode, show all categories. In normal mode, only show categories with visible statistics
    const shouldShow = isEditingColumns || header.startIndex !== -1;
    console.log(`Category "${header.label}": shouldShow=${shouldShow}, startIndex=${header.startIndex}, isEditingColumns=${isEditingColumns}`);
    return shouldShow;
  });

  // Navigation tabs
  const tabs = (
    <TabBar 
      activeTab="fundamentals" 
      ticker={ticker}
      publicUuid={publicUuid}
    />
  );

  // Handle timeframe change and update URL
  const handleTimeframeChange = (newTimeframe: 'quarterly' | 'annual') => {
    setTimeframe(newTimeframe);
    
    // Update URL parameter without navigating
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('timeframe', newTimeframe);
    
    // Preserve public_uuid if present
    const newSearch = urlParams.toString();
    navigate(`${location.pathname}?${newSearch}`, { replace: true });
  };

  // Timeframe toggle component (matching ListFundamentals)
  const timeframeToggle = (
    <div className="flex items-center">
      <div className="relative inline-flex rounded-md bg-gray-200 dark:bg-border-color p-1">
        <button
          onClick={() => handleTimeframeChange('quarterly')}
          className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-[color,shadow] duration-200 focus:outline-none ${
            timeframe === 'quarterly'
              ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
              : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
          }`}
        >
          Quarterly
        </button>
        <button
          onClick={() => handleTimeframeChange('annual')}
          className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-[color,shadow] duration-200 focus:outline-none ${
            timeframe === 'annual'
              ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
              : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
          }`}
        >
          Annual
        </button>
      </div>
    </div>
  );

  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Alternative: target thead with position sticky using attribute selector */
        table thead[style*="sticky"]::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead[style*="sticky"]::after {
          background-color: rgb(55, 65, 81);
        }
      `
    }} />
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!isFullScreen && <Header sticky={false} />}
      {!isFullScreen && publicUuid && !isAuthenticated && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg overflow-hidden">
          {/* Back button */}
          <div className="p-4">
            <button
              onClick={() => {
                const timeframeParam = timeframe === 'annual' ? 'timeframe=annual' : 'timeframe=quarterly';
                if (publicUuid) {
                  navigate(`/fundamentals?public_uuid=${publicUuid}&${timeframeParam}`);
                } else {
                  navigate(`/fundamentals?${timeframeParam}`);
                }
              }}
              className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
          </div>

          {/* Header */}
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
                {tickerName} ({tickerSymbol})
              </h1>
              {marketType !== null && !isEtfOrEtv && !isEditingColumns ? (
                <button
                  onClick={handleStartEdit}
                  className="transition-colors duration-200 text-gray-600 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
                  title="Edit statistics"
                >
                  <svg className="lucide lucide-pencil" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                    <path d="m15 5 4 4"></path>
                  </svg>
                </button>
              ) : marketType !== null && !isEtfOrEtv && isEditingColumns ? (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCancelEdit}
                    className="text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDoneEdit}
                    className="bg-primary text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors duration-200"
                  >
                    Done
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="border-b border-gray-200 dark:border-border-color">
            <div className="px-4">
              {tabs}
            </div>
          </nav>

          {/* Timeframe Toggle */}
          <div className="px-4 py-4 border-b border-gray-200 dark:border-border-color">
            {timeframeToggle}
          </div>

          {/* Fundamentals Data Table */}
          <div ref={tableScrollContainerRef} className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ 
                  width: `${statisticsColumnWidth}px`, 
                  minWidth: isSmallScreen ? '100px' : '120px', 
                  maxWidth: '250px' 
                }} />
                {((loading || isSyncing) && (!currentColumns || currentColumns.length === 0) ? Array.from({ length: 6 }) : currentColumns).map((_, index) => (
                  <col key={index} style={{ width: `${dateColumnWidth}px`, minWidth: `${dateColumnWidth}px`, maxWidth: `${dateColumnWidth}px` }} />
                ))}
                <col style={{ width: '60px' }} />
              </colgroup>
              
              <thead 
                className="bg-gray-50 dark:bg-background-card sticky-header-border" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 50
                }}
              >
                <tr>
                  <th 
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 40
                    }}
                  >
                    <div className="flex items-center">
                      <span>INDICATORS</span>
                    </div>
                  </th>
                  {(loading || isSyncing) && (!currentColumns || currentColumns.length === 0) ? (
                    // Show empty column headers during loading (no pulsing boxes to avoid alignment issues)
                    Array.from({ length: 6 }).map((_, index) => (
                      <th
                        key={`loading-header-${index}`}
                        className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                      >
                        {/* Empty during loading */}
                      </th>
                    ))
                  ) : (
                    currentColumns.map((column, index) => (
                      <th
                        key={index}
                        className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                        style={{ width: `${dateColumnWidth}px`, minWidth: `${dateColumnWidth}px`, maxWidth: `${dateColumnWidth}px` }}
                      >
                        {column.label}
                      </th>
                    ))
                  )}
                  
                  {/* Full screen button column */}
                  <th 
                    className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    {!isEditingColumns && (
                      <button
                        onClick={() => setIsFullScreen(true)}
                        className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                        title="Open in full screen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              
              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {isSyncing && currentStatistics.length > 0 && currentColumns.length === 0 ? (
                  // Show placeholder rows with loading animation during retry when no columns available yet
                  currentStatistics.map((statistic, statIndex) => {
                    return (
                      <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                        <td 
                          className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 30,
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto'
                          }}
                        >
                          <span className={getStatisticLabelSizeClass(fontSize)}>
                            {statistic.label.split(' ').slice(0, -1).join(' ')}
                            {statistic.label.split(' ').length > 1 && ' '}
                            <span className="whitespace-nowrap">
                              {statistic.label.split(' ').slice(-1)[0]}
                              <button
                                onClick={() => handleIndicatorInfoClick(statistic.key)}
                                className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                title={`Learn more about ${statistic.label}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                  <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            </span>
                          </span>
                        </td>
                        {Array.from({ length: 6 }).map((_, colIndex) => (
                          <td
                            key={`loading-col-${colIndex}`}
                            className={`${colIndex === 0 ? 'pl-1 sm:pl-2 pr-1 sm:pr-2 md:pr-4 lg:pr-6' : 'px-1 sm:px-2 md:px-4 lg:px-6'} py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}
                          >
                            <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                          </td>
                        ))}
                        <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                          {/* Empty cell for full screen button column */}
                        </td>
                      </tr>
                    );
                  })
                ) : (loading || isSyncing) && currentColumns.length === 0 && currentCategoryHeaders.length === 0 ? (
                  // Show skeleton loaders during initial load when no data is available yet
                  currentStatistics.slice(0, 20).map((statistic) => (
                    <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                      <td 
                        className="px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 30
                        }}
                      >
                        <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                      </td>
                      {Array.from({ length: 6 }).map((_, colIndex) => (
                        <td
                          key={`loading-stat-col-${colIndex}`}
                          className={`${colIndex === 0 ? 'pl-1 sm:pl-2 pr-1 sm:pr-2 md:pr-4 lg:pr-6' : 'px-1 sm:px-2 md:px-4 lg:px-6'} py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}
                        >
                          <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                        </td>
                      ))}
                      <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                        {/* Empty cell for full screen button column */}
                      </td>
                    </tr>
                  ))
                ) : currentCategoryHeaders.length === 0 ? (
                  // For ETF/ETV tickers with no category headers, render statistics directly
                  currentStatistics.map((statistic, statIndex) => {
                    const isHidden = currentHiddenStatistics.includes(statistic.key);
                    return (
                      <tr key={statistic.key} className={`h-12 sm:h-16 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                        <td 
                          className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 30,
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto'
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {isEditingColumns && (
                              <button
                                onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 ${
                                  isHidden 
                                    ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                    : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                }`}
                                title={isHidden ? "Show statistic" : "Hide statistic"}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {isHidden ? (
                                    <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                  ) : (
                                    <>
                                      <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                      <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </>
                                  )}
                                </svg>
                              </button>
                            )}
                            <span className={getStatisticLabelSizeClass(fontSize)}>
                              {(() => {
                                const words = statistic.label.split(' ');
                                const lastWord = words[words.length - 1];
                                const otherWords = words.slice(0, -1);
                                
                                return (
                                  <>
                                    {otherWords.join(' ')}
                                    {otherWords.length > 0 && ' '}
                                    <span className="whitespace-nowrap">
                                      {lastWord}
                                      {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                        <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                          PRO
                                        </span>
                                      )}
                                    </span>
                                  </>
                                );
                              })()}
                            </span>
                          </div>
                        </td>
                        {currentColumns.map((column, colIndex) => {
                          const value = getValue(statistic.key, column);
                          const previousValue = colIndex < currentColumns.length - 1 ? getValue(statistic.key, currentColumns[colIndex + 1]) : null;
                          
                          const arrowInfo = getArrowInfo(statistic.key, value, previousValue);
                          const textColor = getStatisticColor(statistic.key, value);
                          const tooltipText = getArrowTooltip(statistic.key, value, previousValue);
                          
                          return (
                            <td
                              key={colIndex}
                              className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} ${textColor} bg-surface-light dark:bg-background-card`}
                              style={{ width: `${dateColumnWidth}px`, minWidth: `${dateColumnWidth}px`, maxWidth: `${dateColumnWidth}px` }}
                            >
                              <span className="inline-flex items-center gap-1">
                                {formatValue(value, statistic.type || 'ratio', statistic.key)}
                                {arrowInfo.direction === 'up' && (
                                  <div className="relative group/arrow">
                                    <UpArrowIcon 
                                      className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                    />
                                    {tooltipText && (
                                      <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                                        {tooltipText}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {arrowInfo.direction === 'down' && (
                                  <div className="relative group/arrow">
                                    <DownArrowIcon 
                                      className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                    />
                                    {tooltipText && (
                                      <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                                        {tooltipText}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </span>
                            </td>
                          );
                        })}
                        
                        {/* Empty cell for full screen button column */}
                        <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                          {/* Empty cell for full screen button column */}
                        </td>
                      </tr>
                    );
                  })
                ) : (loading || isSyncing) && (!currentColumns || currentColumns.length === 0) ? (
                  // Show placeholder rows with loading animation during initial load or retry when no columns available yet
                  currentCategoryHeaders.length > 0 ? currentCategoryHeaders.map((categoryHeader, categoryIndex) => {
                    const categoryStats = currentStatistics.slice(
                      categoryHeader.startIndex,
                      categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length
                    );
                    
                    return (
                      <React.Fragment key={`loading-${categoryHeader.label}`}>
                        {/* Category Header Row */}
                        <tr className="bg-gray-100 dark:bg-border-color">
                          <td
                            className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 30,
                              width: `${statisticsColumnWidth}px`,
                              maxWidth: `${statisticsColumnWidth}px`
                            }}
                          >
                            <div 
                              className="whitespace-nowrap"
                              style={{ overflow: 'visible', position: 'relative' }}
                            >
                              <span className="inline">{categoryHeader.label}</span>
                              <button
                                onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                                className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                title={`Learn more about ${categoryHeader.label}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                  <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                          {Array.from({ length: 6 }).map((_, colIndex) => (
                            <td
                              key={`loading-header-col-${colIndex}`}
                              className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                            >
                              {/* Empty cells for alignment */}
                            </td>
                          ))}
                          <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                            {/* Empty cell for full screen button column */}
                          </td>
                        </tr>
                        
                        {/* Statistics Rows */}
                        {categoryStats.map((statistic) => (
                          <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                            <td 
                              className="px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 30
                              }}
                            >
                              <span className={`whitespace-pre-line ${getStatisticLabelSizeClass(fontSize)}`}>{statistic.label}</span>
                            </td>
                            {Array.from({ length: 6 }).map((_, colIndex) => (
                              <td
                                key={`loading-stat-col-${colIndex}`}
                                className={`px-2 sm:px-4 lg:px-6 py-1 text-right ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                              >
                                <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded ml-auto"></div>
                              </td>
                            ))}
                            <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                              {/* Empty cell for full screen button column */}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }) : null
                ) : currentCategoryHeaders.length > 0 ? (
                  currentCategoryHeaders.map((categoryHeader, categoryIndex) => (
                    <React.Fragment key={categoryHeader.label}>
                      {/* Category Header Row */}
                      <tr className="bg-gray-100 dark:bg-border-color">
                        <td
                          className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 30,
                            width: `${statisticsColumnWidth}px`,
                            maxWidth: `${statisticsColumnWidth}px`
                          }}
                        >
                          <div 
                            className="whitespace-nowrap"
                            style={{ overflow: 'visible', position: 'relative' }}
                          >
                            <span className="inline">{categoryHeader.label}</span>
                            <button
                              onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                              className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                              title={`Learn more about ${categoryHeader.label}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                        {currentColumns.map((_, colIndex) => (
                          <td
                            key={colIndex}
                            className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                          >
                            {/* Empty cells for alignment */}
                          </td>
                        ))}
                        
                        {/* Empty cell for full screen button column */}
                        <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                          {/* Empty cell for full screen button column */}
                        </td>
                      </tr>
                      
                      {/* Statistics Rows */}
                      {currentStatistics
                        .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length)
                        .map((statistic, statIndex) => {
                        const isHidden = currentHiddenStatistics.includes(statistic.key);
                        return (
                          <tr key={statistic.key} className={`h-12 sm:h-16 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                            <td 
                              className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 30,
                                paddingTop: '0px',
                                paddingBottom: '0px',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                hyphens: 'auto'
                              }}
                            >
                              <div className="flex items-start gap-2">
                                {isEditingColumns && (
                                  <button
                                    onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                    className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 mt-0.5 ${
                                      isHidden 
                                        ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                        : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                    }`}
                                    title={isHidden ? "Show statistic" : "Hide statistic"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {isHidden ? (
                                        // Unchecked rounded square (hidden)
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                      ) : (
                                        // Checked rounded square (visible)
                                        <>
                                          <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </>
                                      )}
                                    </svg>
                                  </button>
                                )}
                                <span className={getStatisticLabelSizeClass(fontSize)}>
                                  {statistic.label.split(' ').slice(0, -1).join(' ')}
                                  {statistic.label.split(' ').length > 1 && ' '}
                                  <span className="whitespace-nowrap">
                                    {statistic.label.split(' ').slice(-1)[0]}
                                    {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                        PRO
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleIndicatorInfoClick(statistic.key)}
                                      className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                      title={`Learn more about ${statistic.label}`}
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                        <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                      </svg>
                                    </button>
                                  </span>
                                </span>
                              </div>
                            </td>
                          {currentColumns.map((column, colIndex) => {
                            const value = getValue(statistic.key, column);
                            const previousValue = colIndex < currentColumns.length - 1 ? getValue(statistic.key, currentColumns[colIndex + 1]) : null;
                            
                            // Get arrow direction and color
                            const arrowInfo = getArrowInfo(statistic.key, value, previousValue);
                            const textColor = getStatisticColor(statistic.key, value);
                            const tooltipText = getArrowTooltip(statistic.key, value, previousValue);
                            
                            return (
                              <td
                                key={colIndex}
                                className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} ${textColor} bg-surface-light dark:bg-background-card`}
                              >
                                <span className="inline-flex items-center gap-1">
                                  {formatValue(value, statistic.type || 'ratio', statistic.key)}
                                  {arrowInfo.direction === 'up' && (
                                    <div className="relative group/arrow">
                                      <UpArrowIcon 
                                        className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                      />
                                      {tooltipText && (
                                        <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                                          {tooltipText}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {arrowInfo.direction === 'down' && (
                                    <div className="relative group/arrow">
                                      <DownArrowIcon 
                                        className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                      />
                                      {tooltipText && (
                                        <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                                          {tooltipText}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </span>
                              </td>
                            );
                          })}
                          
                          {/* Empty cell for full screen button column */}
                          <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                            {/* Empty cell for full screen button column */}
                          </td>
                        </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                ) : null}
              </tbody>
            </table>
          </div>
          
          {/* Status Bar */}
          <div className={`px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Full Screen Overlay */}
      {isFullScreen && (
      <div
        className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
        role="dialog"
        aria-modal="true"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ 
                  width: `${statisticsColumnWidth}px`, 
                  minWidth: isSmallScreen ? '100px' : '120px', 
                  maxWidth: '250px' 
                }} />
                {currentColumns.map((_, index) => (
                  <col key={index} style={{ width: `${dateColumnWidth}px`, minWidth: `${dateColumnWidth}px`, maxWidth: `${dateColumnWidth}px` }} />
                ))}
              </colgroup>
              
              {/* Header with sticky positioning */}
              <thead className="sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                <tr>
                  {/* Statistics column header */}
                  <th 
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                    style={{ 
                      width: `${statisticsColumnWidth}px`,
                      minWidth: isSmallScreen ? '100px' : '250px',
                      maxWidth: '400px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 40
                    }}
                  >
                    <div className="flex items-center">
                      <span>Indicators</span>
                    </div>
                  </th>
                  
                  {/* Date columns headers */}
                  {currentColumns.map((column, index) => (
                    <th
                      key={index}
                      className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                      style={{ width: `${dateColumnWidth}px`, minWidth: `${dateColumnWidth}px`, maxWidth: `${dateColumnWidth}px` }}
                    >
                      {column.label}
                    </th>
                  ))}
                  
                  {/* Close button - sticky right */}
                  <th 
                    className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    <button
                      onClick={() => setIsFullScreen(false)}
                      className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                      title="Close full screen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </th>
                </tr>
              </thead>
              
              {/* Table body */}
              <tbody className="bg-white dark:bg-background divide-y divide-gray-200 dark:divide-border-color">
                {currentCategoryHeaders.length === 0 ? (
                  // For ETF/ETV tickers with no category headers, render statistics directly
                  currentStatistics.map((statistic, statIndex) => {
                    const isHidden = currentHiddenStatistics.includes(statistic.key);
                    return (
                      <tr key={statistic.key} className={`h-10 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                        <td 
                          className={`px-2 py-1 text-left ${getStatisticLabelSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`}
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 20,
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto'
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {isEditingColumns && (
                              <button
                                onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 ${
                                  isHidden 
                                    ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                    : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                }`}
                                title={isHidden ? "Show statistic" : "Hide statistic"}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {isHidden ? (
                                    <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                  ) : (
                                    <>
                                      <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                      <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </>
                                  )}
                                </svg>
                              </button>
                            )}
                            <span className={getStatisticLabelSizeClass(fontSize)} style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                              {statistic.label.split(' ').slice(0, -1).join(' ')}
                              {statistic.label.split(' ').length > 1 && ' '}
                              <span className="whitespace-nowrap">
                                {statistic.label.split(' ').slice(-1)[0]}
                                {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                  <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                    PRO
                                  </span>
                                )}
                                <button
                                  onClick={() => handleIndicatorInfoClick(statistic.key)}
                                  className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                  title={`Learn more about ${statistic.label}`}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                    <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </span>
                            </span>
                          </div>
                        </td>
                        {currentColumns.map((column, colIndex) => {
                          const value = getValue(statistic.key, column);
                          const previousValue = colIndex < currentColumns.length - 1 ? getValue(statistic.key, currentColumns[colIndex + 1]) : null;
                          
                          const arrowInfo = getArrowInfo(statistic.key, value, previousValue);
                          
                          return (
                            <td key={colIndex} className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                              <span className="inline-flex items-center gap-1">
                                {formatValue(value, statistic.type || 'ratio', statistic.key)}
                                {arrowInfo.direction === 'up' && (
                                  <div className="relative group/arrow">
                                    <UpArrowIcon 
                                      className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                    />
                                  </div>
                                )}
                                {arrowInfo.direction === 'down' && (
                                  <div className="relative group/arrow">
                                    <DownArrowIcon 
                                      className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                    />
                                  </div>
                                )}
                              </span>
                            </td>
                          );
                        })}
                        
                        {/* Empty cell for close button column */}
                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-text-primary">
                          {/* Empty cell for close button column */}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  currentCategoryHeaders.map((categoryHeader, categoryIndex) => (
                    <React.Fragment key={categoryHeader.label}>
                      {/* Category Header Row */}
                      <tr className="bg-gray-100 dark:bg-border-color">
                        <td
                          className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 30,
                            width: `${statisticsColumnWidth}px`,
                            maxWidth: `${statisticsColumnWidth}px`,
                            paddingTop: '0px',
                            paddingBottom: '0px'
                          }}
                        >
                          <div 
                            className="whitespace-nowrap"
                            style={{ overflow: 'visible', position: 'relative' }}
                          >
                            <span className="inline">{categoryHeader.label}</span>
                            <button
                              onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                              className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                              title={`Learn more about ${categoryHeader.label}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                        {currentColumns.map((_, colIndex) => (
                          <td
                            key={colIndex}
                            className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                          >
                            {/* Empty cells for alignment */}
                          </td>
                        ))}
                        
                        {/* Empty cell for close button column */}
                        <td className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                          {/* Empty cell for close button column */}
                        </td>
                      </tr>
                      
                      {/* Statistics Rows */}
                      {currentStatistics
                        .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length)
                        .map((statistic, statIndex) => {
                        const isHidden = currentHiddenStatistics.includes(statistic.key);
                        return (
                          <tr key={statistic.key} className={`h-10 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                            <td 
                              className={`px-4 py-1 text-left ${getStatisticLabelSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                hyphens: 'auto'
                              }}
                            >
                              <div className="flex items-start gap-2">
                                {isEditingColumns && (
                                  <button
                                    onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                    className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 mt-0.5 ${
                                      isHidden 
                                        ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                        : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                    }`}
                                    title={isHidden ? "Show statistic" : "Hide statistic"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {isHidden ? (
                                        // Unchecked rounded square (hidden)
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                      ) : (
                                        // Checked rounded square (visible)
                                        <>
                                          <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </>
                                      )}
                                    </svg>
                                  </button>
                                )}
                                <span className={getStatisticLabelSizeClass(fontSize)} style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                                  {statistic.label.split(' ').slice(0, -1).join(' ')}
                                  {statistic.label.split(' ').length > 1 && ' '}
                                  <span className="whitespace-nowrap">
                                    {statistic.label.split(' ').slice(-1)[0]}
                                    {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                        PRO
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleIndicatorInfoClick(statistic.key)}
                                      className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                      title={`Learn more about ${statistic.label}`}
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                        <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                      </svg>
                                    </button>
                                  </span>
                                </span>
                              </div>
                            </td>
                            {currentColumns.map((column, colIndex) => {
                              const value = getValue(statistic.key, column);
                              const previousValue = colIndex < currentColumns.length - 1 ? getValue(statistic.key, currentColumns[colIndex + 1]) : null;
                              
                              const arrowInfo = getArrowInfo(statistic.key, value, previousValue);
                              
                              return (
                                <td key={colIndex} className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                                  <span className="inline-flex items-center gap-1">
                                    {formatValue(value, statistic.type || 'ratio', statistic.key)}
                                    {arrowInfo.direction === 'up' && (
                                      <div className="relative group/arrow">
                                        <UpArrowIcon 
                                          className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                        />
                                      </div>
                                    )}
                                    {arrowInfo.direction === 'down' && (
                                      <div className="relative group/arrow">
                                        <DownArrowIcon 
                                          className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                        />
                                      </div>
                                    )}
                                  </span>
                                </td>
                              );
                            })}
                            
                            {/* Empty cell for close button column */}
                            <td className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-text-primary">
                              {/* Empty cell for close button column */}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
        </div>
          
          {/* Status Bar */}
          <div className={`flex-shrink-0 px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
      </div>
      )}
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        categoryName={selectedCategory}
      />
      
      {/* Indicator Info Modal */}
      <IndicatorInfoModal
        isOpen={isIndicatorModalOpen}
        onClose={handleCloseIndicatorModal}
        indicatorKey={selectedIndicatorKey}
      />
    </div>
    </>
  );
};

export default TickerFundamentals;
